import { jsonKeyMapping } from "../Config/Constants";
import { TemplateObject } from "../Models/TemplateObject";

/**
 * Determines if a step is completed by checking if all its form fields have values
 * @param step - The step object containing form group
 * @param formData - The current form data
 * @returns boolean - true if step is completed, false otherwise
 */
export const isStepCompleted = (
  step: TemplateObject,
  formData: TemplateObject
): boolean => {
  // Special case: never auto-mark the injected Review Summary step as completed
  // even though it intentionally has no formGroup (so it would otherwise appear completed).
  // This keeps the navigation from showing a check mark for the review screen.
  if (step[jsonKeyMapping.SLUGIFY] === "review-submit") {
    return false;
  }

  // If step has no form group, consider it completed
  if (
    !step[jsonKeyMapping.FORM_GROUP] ||
    !Array.isArray(step[jsonKeyMapping.FORM_GROUP]) ||
    step[jsonKeyMapping.FORM_GROUP].length === 0
  ) {
    return true;
  }

  // Check each form element in the step
  for (const formElement of step[jsonKeyMapping.FORM_GROUP]) {
    if (!isFormElementCompleted(formElement, formData)) {
      return false;
    }
  }

  return true;
};

/**
 * Checks if a single form element is completed (has a value)
 * @param formElement - The form element to check
 * @param formData - The current form data
 * @returns boolean - true if element is completed, false otherwise
 */
const isFormElementCompleted = (
  formElement: TemplateObject,
  formData: TemplateObject
): boolean => {
  const fieldId = formElement[jsonKeyMapping.FORM_ID];

  // If field has no ID, skip it
  if (!fieldId) {
    return true;
  }

  const fieldValue = formData[fieldId];

  // Check if field has a value
  if (fieldValue === undefined || fieldValue === null || fieldValue === "") {
    return false;
  }

  // For array values (like checkboxes), check if array has items
  if (Array.isArray(fieldValue) && fieldValue.length === 0) {
    return false;
  }

  // Check for dynamically added fields (both "add more" and general dynamic fields)
  const dynamicRegex = new RegExp(`^${fieldId}\\d+$`);
  const dynamicFields = Object.keys(formData).filter((key) =>
    dynamicRegex.test(key)
  );

  // If this field supports "add more" functionality or has dynamic fields, check them
  if (
    formElement[jsonKeyMapping.FORM_ADD_MORE] === "yes" ||
    dynamicFields.length > 0
  ) {
    // Check if any dynamic fields exist and are incomplete
    for (const dynamicKey of dynamicFields) {
      const dynamicValue = formData[dynamicKey];
      if (
        dynamicValue === undefined ||
        dynamicValue === null ||
        dynamicValue === ""
      ) {
        return false;
      }
    }
  }

  // Check nested form elements (like radio buttons with more fields)
  if (formElement[jsonKeyMapping.FORM_INPUT_RADIO_MORE_FIELDS]) {
    for (const nestedElement of formElement[
      jsonKeyMapping.FORM_INPUT_RADIO_MORE_FIELDS
    ]) {
      if (!isFormElementCompleted(nestedElement, formData)) {
        return false;
      }
    }
  }

  // Check radio button options that have conditional more fields
  if (formElement.inputRadio && Array.isArray(formElement.inputRadio)) {
    for (const radioOption of formElement.inputRadio) {
      // Check if this radio option is currently selected
      const isSelected = fieldValue === radioOption.value;

      // If this option is selected and has more fields, check those fields
      if (
        isSelected &&
        radioOption[jsonKeyMapping.FORM_INPUT_RADIO_MORE_FIELDS]
      ) {
        for (const moreField of radioOption[
          jsonKeyMapping.FORM_INPUT_RADIO_MORE_FIELDS
        ]) {
          if (!isFormElementCompleted(moreField, formData)) {
            return false;
          }
        }
      }
    }
  }

  // Check grouped form elements
  if (formElement[jsonKeyMapping.FORM_ADD_MORE_GROUP]) {
    for (const groupElement of formElement[
      jsonKeyMapping.FORM_ADD_MORE_GROUP
    ]) {
      if (!isFormElementCompleted(groupElement, formData)) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Determines if a step has any incomplete fields (for warning icon)
 * @param step - The step object containing form group
 * @param formData - The current form data
 * @returns boolean - true if step has incomplete fields, false otherwise
 */
export const hasIncompleteFields = (
  step: TemplateObject,
  formData: TemplateObject
): boolean => {
  // If step has no form group, no incomplete fields
  if (
    !step[jsonKeyMapping.FORM_GROUP] ||
    !Array.isArray(step[jsonKeyMapping.FORM_GROUP]) ||
    step[jsonKeyMapping.FORM_GROUP].length === 0
  ) {
    return false;
  }

  // Check if any form elements have partial data
  for (const formElement of step[jsonKeyMapping.FORM_GROUP]) {
    const fieldId = formElement[jsonKeyMapping.FORM_ID];

    if (
      fieldId &&
      formData[fieldId] !== undefined &&
      formData[fieldId] !== null &&
      formData[fieldId] !== ""
    ) {
      // If any field has some data but step is not fully completed, show warning
      if (!isStepCompleted(step, formData)) {
        return true;
      }
    }
  }

  return false;
};
